import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AgentConfig } from '../../../types/agent.types';
import {
  AgentInfoPopover as AgentInfoPopover__Root,
  AgentInfoPopover__Name,
  AgentInfoPopover__Version,
  AgentInfoPopover__Description,
  AgentInfoPopover__Tabs,
  AgentInfoPopover__Tab,
  AgentInfoPopover__TabContent,
  AgentInfoPopover__PatchNotes,
  AgentInfoPopover__PatchNote,
} from './AgentInfoPopover.styles';

type TabKey = 'info' | 'patchNotes';

interface AgentInfoPopoverProps {
  agent: AgentConfig;
  anchorRect: DOMRect;
  onClose: () => void;
}

export const AgentInfoPopover: React.FC<AgentInfoPopoverProps> = ({
  agent,
  anchorRect,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('info');

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Position to the right of the sidebar, vertically centered on the anchor
  const top = anchorRect.top + anchorRect.height / 2;
  const left = anchorRect.right + 12;

  const hasPatchNotes = agent.patchNotes && agent.patchNotes.length > 0;

  return createPortal(
    <AgentInfoPopover__Root ref={ref} $top={top} $left={left}>
      <AgentInfoPopover__Name>{agent.name}</AgentInfoPopover__Name>
      {agent.version && (
        <AgentInfoPopover__Version>v{agent.version}</AgentInfoPopover__Version>
      )}

      {hasPatchNotes && (
        <AgentInfoPopover__Tabs>
          <AgentInfoPopover__Tab
            $active={activeTab === 'info'}
            onClick={() => setActiveTab('info')}
          >
            Info
          </AgentInfoPopover__Tab>
          <AgentInfoPopover__Tab
            $active={activeTab === 'patchNotes'}
            onClick={() => setActiveTab('patchNotes')}
          >
            Patch Notes
          </AgentInfoPopover__Tab>
        </AgentInfoPopover__Tabs>
      )}

      <AgentInfoPopover__TabContent>
        {activeTab === 'info' && (
          <AgentInfoPopover__Description>
            {agent.description}
          </AgentInfoPopover__Description>
        )}

        {activeTab === 'patchNotes' && hasPatchNotes && (
          <AgentInfoPopover__PatchNotes>
            {agent.patchNotes!.map((note, i) => (
              <AgentInfoPopover__PatchNote key={i}>
                {note}
              </AgentInfoPopover__PatchNote>
            ))}
          </AgentInfoPopover__PatchNotes>
        )}
      </AgentInfoPopover__TabContent>
    </AgentInfoPopover__Root>,
    document.body,
  );
};
